import { createPortal } from 'react-dom';
import { X, Check, Loader2, Crown } from 'lucide-react';
import { Button, ClubIcon } from '@/components/ui';
import { useLinkClub } from '@/services/mutations/company';
import { useGetClubsB2B2C } from '@/services/queries/club';

interface AddClubModalProps {
	isOpen: boolean;
	onClose: () => void;
	companyId: string;
	linkedClubIds: string[];
}

export const AddClubModal = ({
	isOpen,
	onClose,
	companyId,
	linkedClubIds,
}: AddClubModalProps) => {
	const { data: clubsResponse, isLoading: isLoadingClubs } =
		useGetClubsB2B2C();
	const { mutateAsync: linkClub, isPending: isLinking } = useLinkClub(
		companyId,
		onClose,
	);

	const clubs = Array.isArray(clubsResponse)
		? clubsResponse
		: clubsResponse?.data ?? [];
	const availableClubs = clubs.filter(
		(c: Clubs.Club) => !linkedClubIds.includes(c.id),
	);

	const handleLink = async (clubId: string) => {
		await linkClub(clubId);
	};

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-4 py-4 sm:px-8 sm:py-6">
					<h2 className="text-lg font-black text-stone-900">
						Vincular clube à empresa
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-stone-400 transition-colors hover:text-stone-600"
					>
						<X size={20} />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto p-4 sm:p-8">
					{isLoadingClubs ? (
						<div className="flex items-center justify-center py-8 sm:py-12">
							<Loader2 className="size-8 animate-spin text-stone-300" />
						</div>
					) : availableClubs.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-8 text-center sm:py-12">
							<div className="mb-3 rounded-full bg-stone-100 p-4">
								<Crown size={28} className="text-stone-400" />
							</div>
							<p className="text-sm font-bold text-stone-600">
								Nenhum clube B2B2C disponível
							</p>
							<p className="mt-1 text-xs text-stone-400">
								Todos os clubes B2B2C já estão vinculados ou não
								há clubes cadastrados.
							</p>
						</div>
					) : (
						<div className="space-y-2">
							{availableClubs.map((club: Clubs.Club) => (
								<div
									key={club.id}
									className="group flex flex-col gap-3 rounded-2xl border border-stone-100 bg-stone-50/50 p-4 transition-all hover:border-stone-200 hover:bg-white sm:flex-row sm:items-center sm:justify-between sm:gap-4"
								>
									<div className="flex min-w-0 flex-1 items-center gap-3">
										<ClubIcon club={club} size="md" />
										<div className="flex min-w-0 flex-wrap items-center gap-2">
											<span className="text-sm font-bold text-stone-900">
												{club.name}
											</span>
											<span className="rounded-md border border-purple-100 bg-purple-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-purple-600">
												B2B2C
											</span>
										</div>
									</div>
									<Button
										text={
											isLinking
												? 'Vinculando...'
												: 'Vincular'
										}
										disabled={isLinking}
										className="w-full shrink-0 rounded-full bg-black px-4 py-2 text-xs font-bold text-white shadow-md hover:bg-neutral-800 disabled:opacity-70 sm:w-auto"
										startIcon={
											isLinking ? (
												<Loader2 className="size-3.5 animate-spin" />
											) : (
												<Check className="size-3.5 stroke-[3]" />
											)
										}
										onClick={() => handleLink(club.id)}
									/>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};
